import { Controller } from "@hotwired/stimulus";

/**
 * Renders a reCAPTCHA widget in the given element and stores its ID
 * @param {HTMLElement} element - The element to render the widget in
 */
function renderWidget(element) {
  try {
    const widgetId = window.grecaptcha.render(element, {
      sitekey: element.dataset.sitekey,
    });
    element.dataset.widgetId = widgetId;
  } catch (error) {
    // Widget likely already rendered, can safely ignore
  }
}

/**
 * Manages reCAPTCHA widgets during Turbo navigation
 * 
 * Handles:
 * - Initial widget rendering
 * - API loading (once per page)
 * - Cleanup during navigation
 * - Multiple instances on the same page
 * 
 * Each widget instance maintains its own ID for proper cleanup.
 */
export default class extends Controller {
  connect() {
    if (window.grecaptcha?.render) {
      // API already loaded, render immediately
      renderWidget(this.element);
    } else {
      // Load API if not present
      this.loadApi();
    }
  }

  disconnect() {
    // Remove any existing reCAPTCHA iframes
    if (window?.grecaptcha) { window.grecaptcha.reset(this.element.dataset.widgetId); }
  }


  loadApi() {
    // Only load API once
    if (window?.grecaptcha) {
      return;
    }

    const script = document.createElement("script");
    script.src =
      "https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit";
    script.async = true;
    script.defer = true;

    // Global callback to render all widgets when API loads
    window.onRecaptchaLoad = () => {
      document
        .querySelectorAll('[data-controller="recaptcha"]')
        .forEach((element) => {
          renderWidget(element);
        });
    };

    document.head.appendChild(script);
  }
}
